/**
 * <DeviceCheckStep> — l'étape en cours du test du matériel, dépliée (organism).
 *
 * Dans la liste des quatre étapes, une seule est ouverte : celle dont c'est le tour. Elle se
 * détache sur la lavande, un filet de l'univers à gauche (« c'est ici que ça se passe ») ; le
 * verdict, lui, vit dans la pastille de sa ligne. De haut en bas, dans l'ordre de l'ancien écran :
 *  - sa ligne (`<DeviceCheckRow>`), avec la phrase d'état ;
 *  - l'aperçu de la caméra, juste au-dessus de son conseil (`preview`) ;
 *  - le test du son, son bouton d'abord (`soundTest`) ;
 *  - ce qu'on attend de l'élève : « Dis « allô » à voix haute » (`listening`), sinon le conseil
 *    du moment (`guidance`) ; quand l'étape vient de se régler, le constat seul (`resolvedText`),
 *    sur le vert, et plus aucun geste ;
 *  - le niveau du micro (`micLevel`), qui reste à l'écran pendant le constat : c'est là qu'il
 *    prouve que le micro marche ;
 *  - les seuls gestes que l'app ne peut pas faire à sa place (`actions` : « Autoriser »,
 *    « Ouvrir les réglages », « Couper ma vidéo », « Oui, je me vois bien »…), puis le choix de
 *    l'appareil quand il y en a plusieurs (`devices`) ;
 *  - en pied, « Ça n'a pas marché » (l'essai suivant, là où l'élève ne répond pas déjà par
 *    « Non ») et « Passer cette étape ».
 *
 * La bande est un encart : la pastille de sa ligne y perd son fond (`hooks/useOnInset`).
 *
 * Aucun bouton « revérifier » : la mesure tourne seule, la ligne passe au vert d'elle-même.
 * Contrôlée de bout en bout : l'écran (le test du matériel, ses mesures et son échelle de
 * dépannage) dit tout ce qui s'affiche.
 *
 * Composes DS pieces only — `<DeviceCheckRow>`, `<DeviceCheckGuidance>`, `<LevelBars>`,
 * `<IconBubble>`, `<Button>`, `<TextButton>`, `<OptionCard>`, `<Card>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { LevelBars } from "../../atoms/LevelBars"
import { Text } from "../../atoms/typography/Text"
import { InsetContext } from "../../hooks/useOnInset"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"
import { Card } from "../../molecules/Card"
import { DeviceCheckGuidance, type DeviceCheckGuidanceProps } from "../../molecules/DeviceCheckGuidance"
import { DEVICE_CHECK_BUBBLE, DeviceCheckRow, type UIDeviceCheckLine } from "../../molecules/DeviceCheckRow"
import { OptionCard } from "../../molecules/OptionCard"
import { frenchSpacing } from "../../utils/frenchSpacing"

/** Un geste de l'étape. Reste déclaré ici : il porte un glyphe du DS (`IconName`). */
export type UIDeviceCheckAction = {
    key: string
    label: string
    iconName?: IconName
    /** Le geste attendu (le bouton plein) ou sa réponse contraire (le secondaire). Défaut `primary`. */
    emphasis?: "primary" | "secondary"
    loading?: boolean
    disabled?: boolean
}

/** Un appareil qu'on peut choisir — « Micro intégré », « Casque USB ». */
export type UIDeviceOption = { id: string; label: string }

export type DeviceCheckStepProps = {
    line: UIDeviceCheckLine
    /** L'étape vient de se régler : le constat, à la place du conseil et des gestes. */
    resolvedText?: string
    /** L'aperçu de la caméra (`<CameraPreviewFrame>`), à l'étape caméra. */
    preview?: React.ReactNode
    /** Le test du son (`<DeviceCheckSoundTest>`), à l'étape des haut-parleurs. */
    soundTest?: React.ReactNode
    /** On attend que l'élève parle : la consigne, en grand, collée aux barres. Défaut `false`. */
    listening?: boolean
    /** Le niveau du micro, de 0 à 1, dès que l'accès est donné. Absent : pas de barres. */
    micLevel?: number
    /** Le conseil du moment. */
    guidance?: DeviceCheckGuidanceProps
    actions?: UIDeviceCheckAction[]
    onAction?: (key: string) => void
    /** Plusieurs micros ou caméras : celui qu'on écoute, et les autres. */
    devices?: { options: UIDeviceOption[]; selectedId: string; onSelect: (id: string) => void }
    /** « Ça n'a pas marché » : l'essai suivant. Absent : pas de bouton. */
    onNextAttempt?: () => void
    /** « Passer cette étape ». */
    onSkip: () => void
    testID?: string
}

const TEXTS = {
    listenTitle: "Dis « allô » à voix haute",
    listenDetail: "Je t'écoute : les barres en dessous vont bouger.",
    micLevel: "Le niveau de ton micro",
    nextAttempt: "Ça n'a pas marché",
    skip: "Passer cette étape",
}

/** À partir de cette largeur, le contenu de l'étape s'aligne sous son nom, à côté de la pastille. */
const INDENT_FROM = 520
/** Les deux gestes côte à côte à partir de cette largeur ; empilés sur toute la largeur en deçà. */
const ACTIONS_ROW_FROM = 420
/** Le filet de l'étape en cours. */
const RAIL = 3

export function DeviceCheckStep(props: DeviceCheckStepProps) {
    const {
        line,
        resolvedText,
        preview,
        soundTest,
        listening = false,
        micLevel,
        guidance,
        actions = [],
        onAction,
        devices,
        onNextAttempt,
        onSkip,
        testID,
    } = props
    const indent = useWidthAtLeast(INDENT_FROM)
    const resolved = resolvedText != null && resolvedText !== ""
    const actionsRow = useWidthAtLeast(ACTIONS_ROW_FROM)

    return (
        <View style={styles.band(resolved)} onLayout={indent.onLayout} testID={testID}>
            <InsetContext.Provider value>
                <DeviceCheckRow line={line} />
            </InsetContext.Provider>

            <View style={styles.body(indent.atLeast)} onLayout={actionsRow.onLayout}>
                {preview}
                {!resolved && soundTest}

                {resolved ? (
                    <View style={styles.resolved}>
                        <Icon name="check" size={20} color="success-strong" />
                        <Text size="text-lg" weight="bold" color="success-strong" style={styles.flex}>
                            {resolvedText}
                        </Text>
                    </View>
                ) : listening ? (
                    <Card elevation="none" background="surface-raised" p="3">
                        <View style={styles.listen}>
                            <IconBubble iconName="microphone" size={28} iconSize={14} />
                            <View style={styles.flex}>
                                <Text size="text-lg" weight="bold" color="text-strong">
                                    {frenchSpacing(TEXTS.listenTitle)}
                                </Text>
                                <Text size="text-sm" color="text-muted">
                                    {frenchSpacing(TEXTS.listenDetail)}
                                </Text>
                            </View>
                        </View>
                    </Card>
                ) : (
                    guidance != null && <DeviceCheckGuidance {...guidance} />
                )}

                {micLevel != null && <LevelBars level={micLevel} count={8} accessibilityLabel={TEXTS.micLevel} />}

                {!resolved && actions.length > 0 && (
                    <View style={actionsRow.atLeast ? styles.actionsRow : styles.actionsColumn}>
                        {actions.map((action) => (
                            <Button
                                key={action.key}
                                label={action.label}
                                iconName={action.iconName}
                                variant={action.emphasis === "secondary" ? "outline" : "solid"}
                                loading={action.loading}
                                disabled={action.disabled}
                                fullWidth={!actionsRow.atLeast}
                                onPress={() => onAction?.(action.key)}
                            />
                        ))}
                    </View>
                )}

                {!resolved && devices != null && (
                    <View style={styles.devices}>
                        {devices.options.map((device) => (
                            <OptionCard
                                key={device.id}
                                label={device.label}
                                selected={device.id === devices.selectedId}
                                onPress={() => devices.onSelect(device.id)}
                            />
                        ))}
                    </View>
                )}

                {!resolved && (
                    <View style={styles.foot}>
                        {onNextAttempt != null ? (
                            <Button label={TEXTS.nextAttempt} variant="outline" size="sm" onPress={onNextAttempt} />
                        ) : (
                            <View />
                        )}
                        <TextButton label={TEXTS.skip} color="text-muted" size="sm" onPress={onSkip} />
                    </View>
                )}
            </View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Le seul fond teinté de la liste ; vert le temps du constat, seul instant où l'état prend la bande. */
    band: (resolved: boolean) => ({
        gap: theme.spacing["3"],
        paddingVertical: theme.spacing["4"],
        paddingRight: theme.spacing["4"],
        paddingLeft: theme.spacing["4"] - RAIL,
        borderLeftWidth: RAIL,
        borderLeftColor: resolved ? theme.colors.success : theme.colors.role,
        backgroundColor: resolved ? theme.colors.successLight : theme.colors["surface-subtle"],
    }),
    /** Le contenu s'aligne sous le nom de l'étape au large ; sur un téléphone, il prend toute la largeur. */
    body: (indented: boolean) => ({
        gap: theme.spacing["3"],
        marginLeft: indented ? DEVICE_CHECK_BUBBLE + theme.spacing["3"] : 0,
    }),
    flex: { flex: 1 },
    resolved: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["2"],
    },
    listen: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["3"],
    },
    actionsRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing["2"],
    },
    actionsColumn: {
        gap: theme.spacing["2"],
    },
    devices: {
        gap: theme.spacing["2"],
    },
    foot: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing["2"],
    },
}))
